import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ParamType } from "../../../types/grid.ts";
import { getMediaTypes } from "../../../api/fileUpload.ts";
import type { AdvancedFilterProps } from "./types.ts";

interface CommonCacheItem {
  TypeName?: string;
  Value?: string | number;
  Title?: string;
  sName?: string;
  sFilterValue?: string;
  sDisplayText?: string;
}

interface DropdownOption {
  id: string;
  label: string;
}

interface CBSettings {
  like?: boolean;
  single?: boolean;
  defaultVal?: string;
  flags?: Record<string, number>;
  noAll?: boolean;
  /** Bare token override for the CommonData.Common TypeName lookup. WebAdmin2
   *  treats any unrecognised pipe token as the CB_Values key — e.g.
   *  FilterSettings="Promotion" on column iPromotionId looks up
   *  TypeName="Promotion" instead of "iPromotionId". */
  lookupKey?: string;
}

function parseSettings(filterSettings: string | undefined): CBSettings {
  const result: CBSettings = {};
  if (!filterSettings) return result;
  for (const part of filterSettings.split("|")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const upper = trimmed.toUpperCase();
    if (upper === "LIKE") {
      result.like = true;
      result.single = true;
    } else if (upper === "SINGLE") {
      result.single = true;
    } else if (upper === "NOALL") {
      result.noAll = true;
    } else {
      const eqIdx = trimmed.indexOf("=");
      if (eqIdx > 0) {
        const key = trimmed.substring(0, eqIdx).toUpperCase();
        const val = trimmed.substring(eqIdx + 1);
        if (key === "DEFAULT") {
          result.defaultVal = val;
        } else if (key === "FLAGS") {
          try { result.flags = JSON.parse(val); } catch { /* ignore */ }
        }
        // TRANSLATE is ignored for now
      } else {
        // Bare token = CB_Values TypeName override (first wins).
        if (!result.lookupKey) result.lookupKey = trimmed;
      }
    }
  }
  return result;
}

/** Build options from Flags dict: keys are display labels, values are server IDs */
function buildFlagOptions(flags: Record<string, number>): { options: DropdownOption[]; defaultId: string } {
  const options: DropdownOption[] = [];
  let defaultId = "";
  for (const [label, serverVal] of Object.entries(flags)) {
    if (label === "default") {
      defaultId = String(serverVal);
      continue;
    }
    options.push({ id: String(serverVal), label });
  }
  options.sort(sortWithDeletedLast);
  return { options, defaultId };
}

/**
 * Sort comparator for dropdown labels. Entries whose label starts with ❌
 * (deleted / disabled markers) sink to the end of the list; the rest follow
 * a normal locale-aware alphabetical order.
 */
function sortWithDeletedLast(a: { label: string }, b: { label: string }): number {
  const aX = a.label.startsWith("❌");
  const bX = b.label.startsWith("❌");
  if (aX !== bX) return aX ? 1 : -1;
  return a.label.localeCompare(b.label);
}

/**
 * Hardcoded column-specific options — mirrors WebAdmin2's comboboxfilter.ts
 * switch(info.ColumnName.toUpperCase()) cases for columns whose options are
 * NOT stored in S_Admin_CB_Values / CommonData.Common.
 */
const COLUMN_SPECIFIC_OPTIONS: Record<string, DropdownOption[]> = {
  SLASTSTATUS: [
    { id: "MAINT", label: "Error" },
    { id: "MANAGER", label: "Kiosk Manager" },
    { id: "OK", label: "OK" },
    { id: "UNKNOWN", label: "Unknown" },
  ],
  SONLINESTATUS: [
    { id: "ONLINE", label: "Online" },
    { id: "OFFLINE", label: "Offline" },
  ],
  SDISCSTATUS: [
    { id: "OUTGOING INTERNAL LOST", label: "OUTGOING INTERNAL LOST" },
    { id: "WAREHOUSE", label: "WAREHOUSE" },
    { id: "IN- TRANSIT RETURNS", label: "IN- TRANSIT RETURNS" },
    { id: "MAX CHARGE", label: "MAX CHARGE" },
    { id: "SOLD FROM KIOSK", label: "SOLD FROM KIOSK" },
    { id: "INCOMING INTERNAL LOST", label: "INCOMING INTERNAL LOST" },
    { id: "IN- TRANSIT", label: "IN- TRANSIT" },
    { id: "ACTIVE", label: "ACTIVE" },
    { id: "SCANNED AS RETURNED TO SUPPLIER", label: "SCANNED AS RETURNED TO SUPPLIER" },
  ],
  DATATYPE: [
    { id: "1", label: "System.String" },
    { id: "2", label: "System.Integer" },
    { id: "3", label: "System.DateTime" },
    { id: "4", label: "System.Decimal" },
    { id: "5", label: "System.Boolean" },
  ],
  STATEMENTAUDITACTION: [
    { id: "SAVE", label: "Save" },
    { id: "DEACTIVATE", label: "Deactivate" },
    { id: "SEED", label: "Seed" },
  ],
  STATEMENTSCOPE: [
    { id: "DATABASE", label: "Database" },
    { id: "OPERATOR", label: "Operator" },
    { id: "KIOSK", label: "Kiosk" },
  ],
};

/** Build options from CommonData.Common cache */
function buildCommonOptions(cache: AdvancedFilterProps["cache"], lookupKey: string): { options: DropdownOption[]; defaultId: string } {
  const common = (cache.CommonData?.Common ?? []) as CommonCacheItem[];
  const key = lookupKey.toLowerCase();
  const options = common
    .filter((item) => {
      const name = item.TypeName ?? item.sName;
      return typeof name === "string" && name.toLowerCase() === key;
    })
    .map((item) => {
      const id = String(item.Value ?? item.sDisplayText ?? item.sFilterValue ?? "");
      const label = String(item.Title ?? item.sFilterValue ?? item.sDisplayText ?? item.Value ?? "");
      return { id, label };
    })
    .filter((item) => item.id.length > 0 || item.label.length > 0)
    .sort(sortWithDeletedLast);
  return { options, defaultId: options[0]?.id ?? "" };
}

/** Build options from cache fields outside CommonData.Common (WebAdmin2 migration).
 *  Matches on ColumnName first, then on the FilterSettings bare-token key
 *  (e.g. FilterSettings="Promotion" → cache.Promotions). */
function buildCacheSpecificOptions(
  cache: AdvancedFilterProps["cache"],
  columnName: string,
  lookupKey: string | undefined,
): DropdownOption[] | null {
  const key = (lookupKey ?? columnName).toUpperCase();
  switch (key) {
    case "TESTNAME": {
      const tests = (cache.ApprovalKioskTests ?? []) as Array<{ Name: string }>;
      return tests.map((x) => ({ id: x.Name, label: x.Name })).sort(sortWithDeletedLast);
    }
    case "PROMOTION": {
      const promos = (cache.Promotions ?? []) as Array<{ PromotionID: number; Title: string }>;
      return promos
        .filter((p) => typeof p.PromotionID === "number" && typeof p.Title === "string")
        .map((p) => ({ id: String(p.PromotionID), label: `${p.PromotionID}, ${p.Title}` }))
        .sort(sortWithDeletedLast);
    }
    default:
      return null;
  }
}

/** Columns whose options must be fetched asynchronously */
const ASYNC_COLUMN_KEYS = new Set(["IMEDIATYPE", "MEDIATYPE", "IKIOSKMEDIATYPEID"]);

export function ComboBoxFilter({ info, isParameter, value, onChange, onSearch, cache, inline }: AdvancedFilterProps) {
  const settings = useMemo(() => parseSettings(info.FilterSettings), [info.FilterSettings]);

  // Async-loaded options for columns like MediaType (not in cache)
  const [asyncOptions, setAsyncOptions] = useState<DropdownOption[] | null>(null);
  const isAsync = ASYNC_COLUMN_KEYS.has(info.ColumnName.toUpperCase());

  // Dedupe by isAsync — StrictMode double-mount would fire getMediaTypes twice.
  const lastAsyncLoadRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isAsync) return;
    if (lastAsyncLoadRef.current === isAsync) return;
    lastAsyncLoadRef.current = isAsync;
    getMediaTypes()
      .then((types) => {
        setAsyncOptions(types.map((t) => ({ id: String(t.id), label: t.value })));
      })
      .catch(() => { /* already logged by client */ });
  }, [isAsync]);

  const { options } = useMemo(() => {
    // Async-loaded options take priority when available
    if (isAsync && asyncOptions) return { options: asyncOptions, defaultId: "" };
    if (isAsync) return { options: [] as DropdownOption[], defaultId: "" }; // loading

    // Flags mode – options are fully defined in FilterSettings
    if (settings.flags) return buildFlagOptions(settings.flags);

    // Column-specific hardcoded options (mirrors WebAdmin2 comboboxfilter.ts)
    const colSpecific = COLUMN_SPECIFIC_OPTIONS[info.ColumnName.toUpperCase()];
    if (colSpecific) return { options: colSpecific, defaultId: "" };

    // Cache-specific lookups (WebAdmin2 migration: TESTNAME, Promotion, etc.)
    const cacheSpecific = buildCacheSpecificOptions(cache, info.ColumnName, settings.lookupKey);
    if (cacheSpecific) return { options: cacheSpecific, defaultId: "" };

    // Common cache mode – ColumnFormat carries the cache TypeName (e.g.
    // "iOwnerOperatorID|int"); fall back to ColumnName if not set.
    const primaryKey = info.ColumnFormat
      ? (info.ColumnFormat.split("|")[0] ?? info.ColumnName)
      : info.ColumnName;
    const primary = buildCommonOptions(cache, primaryKey);
    // Fallback: some page-defs carry the CB-list key in FilterSettings instead of
    // ColumnFormat (WebAdmin2-style, e.g. FilterSettings="sChain|int" on a column
    // named "Chain"). Only consulted when the primary lookup found nothing, so it
    // can never override a working combo or a real directive token.
    if (primary.options.length === 0 && settings.lookupKey
        && settings.lookupKey.toLowerCase() !== primaryKey.toLowerCase()) {
      const fallback = buildCommonOptions(cache, settings.lookupKey);
      if (fallback.options.length > 0) return fallback;
    }
    return primary;
  }, [settings.flags, settings.lookupKey, info.ColumnFormat, info.ColumnName, info.FilterType, info.FilterSettings, cache, isAsync, asyncOptions]);

  // CheckComboBox lets the user pick multiple values; the trigger shows a
  // count and the dropdown renders checkboxes. Emitted oValue is a CSV.
  const multi = info.FilterType?.toUpperCase() === "FILTERCHECKCOMBOBOX";
  const paramType = isParameter ? ParamType.Specified : settings.like ? ParamType.Like : ParamType.In;

  const [selected, setSelected] = useState(() => {
    if (value.length > 0 && value[0]?.oValue != null) return String(value[0].oValue);
    return "";
  });

  // If we arrive with an entry in `value` (even an empty/Unused one from chip
  // removal), treat that as "explicitly set" and don't overwrite with the
  // configured default on mount. Matches DateFilter's mountedWithValues guard.
  const mountedWithValues = useRef(value.length > 0);

  // Auto-select default on mount
  const didEmitDefault = useRef(false);
  useEffect(() => {
    if (didEmitDefault.current || options.length === 0) return;
    didEmitDefault.current = true;
    if (mountedWithValues.current) return;
    if (!selected && settings.defaultVal) {
      setSelected(settings.defaultVal);
      onChange([{ sColumn: info.ColumnName, oValue: settings.defaultVal, Type: paramType }]);
    }
  }, [options, selected, info.ColumnName, paramType, settings.defaultVal, onChange]);

  // Clear when value is externally reset to a fully empty array (Clear button).
  // Chip removal keeps the entry but marks it Unused — that path doesn't trip
  // this and also doesn't re-emit defaults, which is the desired behaviour.
  useEffect(() => {
    if (value.length === 0 && selected) {
      setSelected("");
      didEmitDefault.current = false;
      mountedWithValues.current = false;
    }
  }, [value, selected]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSearch();
    },
    [onSearch],
  );

  // Don't render a filter with no options (hides broken column configs like orphaned "name" filter)
  if (options.length === 0 && !isAsync) return null;

  return (
    <SearchableSelect
      options={options}
      selected={selected}
      noAll={settings.noAll}
      multi={multi}
      inline={inline}
      onChange={(v) => {
        setSelected(v);
        if (v) {
          onChange([{ sColumn: info.ColumnName, oValue: v, Type: paramType }]);
        } else {
          onChange([]);
        }
      }}
      onKeyDown={handleKeyDown}
      ariaLabel={`Filter by ${info.Header ?? info.ColumnName}`}
    />
  );
}

/* ── Searchable Select ──────────────────────────────────── */
interface SearchableSelectProps {
  options: DropdownOption[];
  selected: string;
  noAll?: boolean;
  /** When true, the dropdown renders checkboxes and the emitted value is a
   *  CSV of selected ids. */
  multi?: boolean;
  /** When true, the trigger fills its parent cell (100% width) instead of the
   *  fixed 160px used by the advanced filter bar. */
  inline?: boolean;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  ariaLabel: string;
}

function SearchableSelect({ options, selected, noAll, multi, inline, onChange, onKeyDown, ariaLabel }: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Compute fixed coords when opening so the dropdown escapes clipping parents
  // (e.g. modal body with overflow: auto).
  useEffect(() => {
    if (!open || !inputRef.current) { setMenuCoords(null); return; }
    const r = inputRef.current.getBoundingClientRect();
    setMenuCoords({ top: r.bottom + 2, left: r.left, width: r.width });
  }, [open]);

  // Portal target: the nearest <dialog> ancestor if we're inside a modal
  // (native <dialog> uses the top layer, so a document.body portal would
  // render behind it). Falls back to document.body otherwise.
  const portalTarget = useMemo(() => {
    if (typeof document === "undefined") return null;
    const dlg = wrapRef.current?.closest("dialog");
    return dlg ?? document.body;
    // Recompute when open toggles so we pick up the dialog that opened after us.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedSet = useMemo(() => {
    if (!multi) return new Set<string>();
    return new Set(selected ? selected.split(",").map((x) => x.trim()).filter(Boolean) : []);
  }, [selected, multi]);

  const selectedLabel = useMemo(() => {
    if (!selected) return "";
    if (multi) {
      if (selectedSet.size === 0) return "";
      if (selectedSet.size === 1) {
        const id = [...selectedSet][0]!;
        return options.find((o) => o.id === id)?.label ?? id;
      }
      return `${selectedSet.size} selected`;
    }
    return options.find((o) => o.id === selected)?.label ?? selected;
  }, [selected, options, multi, selectedSet]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return term ? options.filter(o => o.label.toLowerCase().includes(term)) : options;
  }, [options, search]);

  useEffect(() => {
    if (activeIdx < 0 || !listRef.current) return;
    const offset = noAll ? 0 : 1; // account for "All" item
    const el = listRef.current.children[activeIdx + offset] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, noAll]);

  const close = useCallback(() => { setOpen(false); setSearch(""); setActiveIdx(-1); }, []);

  // Close on outside click. listRef may be portalled outside wrapRef's tree,
  // so check both before closing.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inWrap = wrapRef.current?.contains(target);
      const inList = listRef.current?.contains(target);
      if (!inWrap && !inList) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); return; }
    if (e.key === "Enter") {
      if (open && activeIdx >= 0) {
        e.preventDefault();
        const item = filtered[activeIdx];
        if (item) { onChange(item.id); close(); }
      } else if (open && activeIdx === -1 && !noAll) {
        e.preventDefault();
        onChange(""); close();
      } else {
        onKeyDown(e); // propagate Enter for search
      }
      return;
    }
    if (!open) setOpen(true);
  };

  const select = (id: string) => {
    if (multi) {
      // Empty id = "All" sentinel → clear the whole selection.
      if (!id) { onChange(""); return; }
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id); else next.add(id);
      onChange([...next].join(","));
      // Keep popover open so user can tick more entries.
      return;
    }
    onChange(id);
    close();
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: inline ? "100%" : "160px" }}>
      <input
        ref={inputRef}
        type="text"
        value={open ? search : selectedLabel || (noAll ? "" : "-- All --")}
        placeholder="Search..."
        aria-label={ariaLabel}
        onChange={e => { setSearch(e.target.value); setActiveIdx(-1); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(""); }}
        onKeyDown={handleInputKeyDown}
        style={{
          width: "100%",
          padding: "2px 22px 2px 4px",
          fontSize: "var(--text-sm)",
          fontFamily: "var(--font-family-input)",
          border: "1px solid var(--color-border-default)",
          borderRadius: "var(--radius-1)",
          background: "var(--color-bg)",
          color: open ? "var(--color-text)" : selected ? "var(--color-text)" : "var(--color-text-muted)",
          boxSizing: "border-box",
          outline: open ? "2px solid var(--color-border-focus)" : "none",
          outlineOffset: "-1px",
        }}
      />
      <span
        style={{
          position: "absolute",
          right: "6px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          fontSize: "10px",
          color: "var(--color-text-muted)",
        }}
      >▾</span>
      {open && menuCoords && createPortal(
        <ul
          ref={listRef}
          style={{
            position: "fixed",
            top: menuCoords.top,
            left: menuCoords.left,
            width: menuCoords.width,
            margin: 0,
            padding: 0,
            listStyle: "none",
            maxHeight: "240px",
            overflowY: "auto",
            background: "var(--color-bg)",
            border: "1px solid var(--color-border-default)",
            borderRadius: "var(--radius-1)",
            boxShadow: "var(--shadow-md)",
            zIndex: 1000,
            fontSize: "var(--text-sm)",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {!noAll && (
            <li
              onMouseDown={() => select("")}
              style={{
                padding: "4px 8px",
                cursor: "pointer",
                fontStyle: "italic",
                color: "var(--color-text-muted)",
                background: activeIdx === -1 && !selected ? "var(--color-primary-muted)" : undefined,
              }}
            >{multi ? "-- Clear all --" : "-- All --"}</li>
          )}
          {filtered.map((opt, i) => {
            const isSelected = multi ? selectedSet.has(opt.id) : opt.id === selected;
            return (
              <li
                key={opt.id}
                onMouseDown={() => select(opt.id)}
                style={{
                  padding: "4px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: i === activeIdx || isSelected ? "var(--color-primary)" : "var(--color-text)",
                  background: i === activeIdx ? "var(--color-primary-muted)" : isSelected ? "var(--color-primary-muted)" : undefined,
                }}
              >
                {multi && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    style={{ margin: 0, pointerEvents: "none" }}
                  />
                )}
                <span>{opt.label}</span>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li style={{ padding: "4px 8px", color: "var(--color-text-muted)", fontStyle: "italic" }}>No matches</li>
          )}
        </ul>,
        portalTarget ?? document.body,
      )}
    </div>
  );
}
